import React from 'react'
import { FilterInstance, useDetectionExplorerStore } from '../../DetectionExplorerStore'
import { DETECTION_EXPLORER_FILTER_DEFINITIONS } from '../../config'
import { useDeviceFilterOptions } from '../useDeviceFilterOptions'
import { FilterChipUI } from './FilterChipUI'

const toStoredScalar = (raw: string, scale: number): number | undefined => {
    if (raw === '') return undefined
    const parsed = Number(raw)
    if (Number.isNaN(parsed)) return undefined
    return parsed / scale
}

const toDisplayScalar = (stored: number | undefined, scale: number): string => {
    if (stored === undefined) return ''
    return String(Math.round(stored * scale))
}

export type FilterChipContainerProps = {
    instance: FilterInstance
}

export const FilterChipContainer: React.FC<FilterChipContainerProps> = ({ instance }) => {
    const definition = DETECTION_EXPLORER_FILTER_DEFINITIONS[instance.key]
    const updateActiveFilter = useDetectionExplorerStore((s) => s.updateActiveFilter)
    const removeActiveFilter = useDetectionExplorerStore((s) => s.removeActiveFilter)
    const deviceOptions = useDeviceFilterOptions()

    // The device filter's options are dynamic (built from the lot + learned device ids); every other
    // multiSelect carries its options statically in the definition.
    const multiSelectOptions =
        definition.input.type !== 'multiSelect'
            ? []
            : instance.key === 'device'
              ? deviceOptions
              : definition.input.options

    const textValue = definition.input.type === 'singleText' && typeof instance.value === 'string' ? instance.value : ''
    const multiSelectValue =
        definition.input.type === 'multiSelect' && Array.isArray(instance.value) ? instance.value : []
    const rangeObj =
        definition.input.type === 'numberRange' && !Array.isArray(instance.value) && typeof instance.value === 'object'
            ? instance.value
            : {}

    const scale = definition.input.type === 'numberRange' ? definition.input.displayScale : 1
    const rangeMin = toDisplayScalar(rangeObj.min, scale)
    const rangeMax = toDisplayScalar(rangeObj.max, scale)

    const handleTextChange = (value: string) => updateActiveFilter(instance.id, value)
    const handleMultiSelectChange = (value: string[]) => updateActiveFilter(instance.id, value)
    const handleRangeMinChange = (raw: string) =>
        updateActiveFilter(instance.id, { ...rangeObj, min: toStoredScalar(raw, scale) })
    const handleRangeMaxChange = (raw: string) =>
        updateActiveFilter(instance.id, { ...rangeObj, max: toStoredScalar(raw, scale) })

    return (
        <FilterChipUI
            definition={definition}
            multiSelectOptions={multiSelectOptions}
            textValue={textValue}
            multiSelectValue={multiSelectValue}
            rangeMin={rangeMin}
            rangeMax={rangeMax}
            onTextChange={handleTextChange}
            onMultiSelectChange={handleMultiSelectChange}
            onRangeMinChange={handleRangeMinChange}
            onRangeMaxChange={handleRangeMaxChange}
            onRemove={() => removeActiveFilter(instance.id)}
        />
    )
}
